/**
 * TRAC-1035 — the still-trying message, drawn as one compact line under the
 * "Connecting you to our … team now…" heading while the customer waits for a rep.
 *
 * Rendered for the whole wait, empty until the message is saved: the empty line holds the
 * space the full one needs (see .handoffWaitStatus in ChatInterface.module.css), so nothing
 * below the heading moves when it fills in. The same element stays mounted across the change,
 * so as a status region it is announced when the words arrive.
 */
import { useRef } from 'react';
import { handoffSlowStatus } from '../../constants/humanContact';
import styles from './ChatInterface.module.css';

interface Props {
  department: string | null;
  /** False: hold the line's place, say nothing yet. */
  shown: boolean;
}

export default function HandoffWaitStatusLine({ department, shown }: Props) {
  const { lead, call } = handoffSlowStatus(department);
  const leadText = `${lead} · `;
  const text = `${leadText}${call}`;
  // The space this line held when the wait began stays held for the rest of it. A customer who
  // typed before pressing Connect has no team yet: the line holds the longer both-numbers text
  // (two lines on a narrow panel), and the backend names the team a moment later. Holding only
  // the new, shorter text would pull the waiting card up then; holding both keeps the taller.
  // The row mounts once per attempt, so this is that attempt's first text.
  const first = useRef(text).current;
  return (
    <div
      className={styles.handoffWaitStatus}
      role="status"
      data-handoff-wait-status=""
      data-reserve={shown ? undefined : text}
      data-reserve-first={first !== text ? first : undefined}
    >
      {shown && (
        <span className={styles.handoffWaitStatusText}>
          <span>{leadText}</span>
          <span className={styles.handoffWaitStatusCall}>{call}</span>
        </span>
      )}
    </div>
  );
}
